import React, { useEffect } from "react";
import { Form, Input, Checkbox, Modal } from "semantic-ui-react";
import { FlexRow } from "../../components";
import { AppButton, AppPrimaryButton, FormDropdownField } from "elements";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { MenuSettingService } from "utility/services/menu-setting";

const menuSchema = yup.object().shape({
    Title: yup.string().required("Title is required"),
    Route: yup.string().required("Route is required"),
    SortOrder: yup
        .number()
        .typeError("Sort Order must be number")
        .required("Sort Order is required"),
    ParentMenuId: yup.mixed().nullable(),
    IsVisible: yup.boolean(),
    IsActive: yup.boolean(),
});

const initialMenuState = {
    ParentMenuId: null,
    MenuKey: "",
    Title: "",
    Route: "",
    IconKey: "",
    SortOrder: 1,
    IsVisible: true,
    IsActive: true,
};

const MenuModal = ({ open, onClose, editItem, refresh, options, data }) => {
    const isEditing = !!editItem;

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: initialMenuState,
        resolver: yupResolver(menuSchema),
    });

    const watchTitle = watch("Title");
    const watchParentMenuId = watch("ParentMenuId");

    useEffect(() => {
        if (watchTitle) {
            const cleanTitle = watchTitle.replace(/[/%]/g, "");
            const words = cleanTitle.trim().split(/\s+/).filter(Boolean);
            const camelCaseSuffix = words
                .map((word, index) => {
                    const lowerWord = word.toLowerCase();
                    if (index === 0) {
                        return lowerWord;
                    }
                    return lowerWord.charAt(0).toUpperCase() + lowerWord.slice(1);
                })
                .join("");

            let parentPrefix = "";
            if (watchParentMenuId) {
                const parent = data?.find(m => m.MenuId === Number(watchParentMenuId));
                if (parent && parent.Title) {
                    const cleanParentTitle = parent.Title.replace(/[/%]/g, "");
                    const parentWords = cleanParentTitle.trim().split(/\s+/).filter(Boolean);
                    parentPrefix = parentWords
                        .map((word, index) => {
                            const lowerWord = word.toLowerCase();
                            if (index === 0) {
                                return lowerWord;
                            }
                            return lowerWord.charAt(0).toUpperCase() + lowerWord.slice(1);
                        })
                        .join("");
                }
            }

            const prefix = parentPrefix ? `menu.${parentPrefix}.` : "menu.";
            setValue("MenuKey", `${prefix}${camelCaseSuffix}`);
        } else {
            setValue("MenuKey", "");
        }
    }, [watchTitle, watchParentMenuId, data, setValue]);

    useEffect(() => {
        if (editItem) {
            reset(editItem);
        } else {
            reset(initialMenuState);
        }
    }, [editItem, open, reset]);

    const onSubmit = async (formData) => {
        try {
            if (isEditing && editItem?.MenuId) {
                await MenuSettingService.update(editItem.MenuId, formData);
            } else {
                await MenuSettingService.create(formData);
            }

            refresh();
            onClose();
            reset(initialMenuState);
        } catch (error) {
            console.log(error);
        }
    };

    return (
        <Modal open={open} onClose={onClose} size="small">
            <Modal.Header>
                {isEditing ? "Edit Menu" : "Add Menu"}
            </Modal.Header>
            <Modal.Content>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <Form.Group widths="equal">
                        {/* Title */}
                        <Form.Field error={!!errors.Title}>
                            <label>Title</label>
                            <Controller
                                name="Title"
                                control={control}
                                render={({ field }) => (
                                    <Input placeholder="Enter Title" {...field} />
                                )}
                            />
                            {errors.Title && (
                                <span className="text-red-500">
                                    {errors.Title.message}
                                </span>
                            )}
                        </Form.Field>

                        {/* Menu Key */}
                        <Form.Field>
                            <label>Menu Key</label>
                            <Controller
                                name="MenuKey"
                                control={control}
                                render={({ field }) => (
                                    <Input {...field} readOnly />
                                )}
                            />
                        </Form.Field>

                        <Form.Field>
                            <FormDropdownField
                                control={control}
                                label="Parent Menu"
                                name="ParentMenuId"
                                placeholder="Select Parent Menu"
                                errors={errors?.ParentMenuId}
                                options={options}
                                defaultValue={null}
                                search
                            />
                        </Form.Field>
                    </Form.Group>

                    <Form.Group widths="equal">
                        <Form.Field error={!!errors.Route}>
                            <label>Route</label>
                            <Controller
                                name="Route"
                                control={control}
                                render={({ field }) => (
                                    <Input placeholder="/route-path" {...field} />
                                )}
                            />
                            {errors.Route && (
                                <span className="text-red-500">
                                    {errors.Route.message}
                                </span>
                            )}
                        </Form.Field>

                        <Form.Field>
                            <label>Icon Key</label>
                            <Controller
                                name="IconKey"
                                control={control}
                                render={({ field }) => (
                                    <Input placeholder="Icon key" {...field} />
                                )}
                            />
                        </Form.Field>

                        <Form.Field error={!!errors.SortOrder}>
                            <label>Sort Order</label>
                            <Controller
                                name="SortOrder"
                                control={control}
                                render={({ field }) => (
                                    <Input type="number" {...field} />
                                )}
                            />
                            {errors.SortOrder && (
                                <span className="text-red-500">
                                    {errors.SortOrder.message}
                                </span>
                            )}
                        </Form.Field>
                    </Form.Group>

                    <Form.Group>
                        <Form.Field>
                            <label>Is Visible</label>
                            <Controller
                                name="IsVisible"
                                control={control}
                                render={({ field }) => (
                                    <Checkbox
                                        toggle
                                        checked={field.value}
                                        onChange={(e, data) =>
                                            field.onChange(data.checked)
                                        }
                                    />
                                )}
                            />
                        </Form.Field>

                        <Form.Field>
                            <label>Is Active</label>
                            <Controller
                                name="IsActive"
                                control={control}
                                render={({ field }) => (
                                    <Checkbox
                                        toggle
                                        checked={field.value}
                                        onChange={(e, data) =>
                                            field.onChange(data.checked)
                                        }
                                    />
                                )}
                            />
                        </Form.Field>
                    </Form.Group>

                    <FlexRow className="justify-content-end">
                        <AppPrimaryButton type="submit">
                            {isEditing ? "Update Menu" : "Save Menu"}
                        </AppPrimaryButton>
                        <AppButton type="button" onClick={onClose}>
                            Cancel
                        </AppButton>
                    </FlexRow>
                </Form>
            </Modal.Content>
        </Modal>
    );
};

export default MenuModal;
